import { produce } from 'immer'
import { last, uniq } from 'lodash-es'

import { ASRUpdateStreamMessage } from 'src/network/StreamClient'
import { rollbarService } from 'src/services/RollbarService'

import { TranscriptWord, ASRTranscriptWord, ASRChunkWordJSON, isPunctuation } from './TranscriptWord'

export type TranscriptParagraphOld = {
    id: string
    authorId: string
    time: number
    timeAbsolute: number
    duration: number
    words: TranscriptWord[]
    updatedAt: number
}

export type ASRTranscriptParagraph = {
    uttIds: number[]
} & TranscriptParagraphOld

export type TranscriptAuthor = {
    id: string
    name: string
}

export type TranscriptOld = {
    paragraphs: TranscriptParagraphOld[]
    asrParagraphs: ASRTranscriptParagraph[]
    words: Record<string, Array<TranscriptWord | ASRTranscriptWord>>
    authors: Record<string, TranscriptAuthor>
    isDiarizationSupported: boolean
    recordingStartedAt: Date
    processedTime: number
    asrProcessedTime?: number
    lastRevisionUpdateDiff?: {
        removed: Array<TranscriptParagraphOld | ASRTranscriptParagraph>
        added: Array<TranscriptParagraphOld | ASRTranscriptParagraph>
    }
    lastAsrUpdateDiff?: {
        removed: Array<TranscriptParagraphOld | ASRTranscriptParagraph>
        added: Array<TranscriptParagraphOld | ASRTranscriptParagraph>
    }
    unfinishedUtteranceId: number | null
}

export type TranscriptJSON = {
    data: {
        paragraphs: Array<{
            id: string
            author_id: string
            time: number
            epoch_time: number
            duration: number
        }>
        words: Array<{
            text: string
            paragraph_id: string
            time: number
            duration: number
        }>
        authors: Array<{
            id: string
            text: string
        }>
    }
    processed_time: number
    recording_started_at: string
}

export type ASRChunkJSON = {
    rec_id: number
    utt_id: number
    rec_ended: boolean
    utt_ended: boolean
    start: number
    end: number
    word_list: ASRChunkWordJSON[]
    start_epoch: number
}

export type DrainOutcome = {
    drained: number
    added: number
    changed: number
}

export const TranscriptOld = {
    EMPTY: {
        words: {},
        paragraphs: [],
        authors: {},
        isDiarizationSupported: false,
        asrParagraphs: [],
        recordingStartedAt: new Date(),
        processedTime: 0,
        unfinishedUtteranceId: null,
    } as TranscriptOld,

    isValidJSON: (json: TranscriptJSON): boolean => {
        return Array.isArray(json?.data?.words) && Array.isArray(json?.data?.paragraphs) && Array.isArray(json?.data?.authors)
    },

    fromJSON: (json: TranscriptJSON): TranscriptOld => {
        const words = json.data.words.reduce<TranscriptOld['words']>((map, w) => {
            map[w.paragraph_id] = (map[w.paragraph_id] ?? []).concat({ ...w, paragraphId: w.paragraph_id })

            return map
        }, {})
        const isDiarizationSupported = !!json.data.authors.filter(({ text }) => !!text).length

        return {
            words,
            paragraphs: json.data.paragraphs
                .filter((p) => p.duration)
                .map(({ author_id, ...p }) => ({
                    ...p,
                    authorId: author_id,
                    words: words[p.id],
                    updatedAt: Date.now(),
                    timeAbsolute: p.epoch_time,
                })),
            asrParagraphs: [],
            authors: json.data.authors.reduce<TranscriptOld['authors']>((map, a) => {
                map[a.id] = { ...a, name: a.text }

                return map
            }, {}),
            isDiarizationSupported,
            processedTime: json.processed_time,
            recordingStartedAt: new Date(json.recording_started_at),
            unfinishedUtteranceId: null,
        }
    },

    /**
     * Calculates the exact timestamp for the processed_time.
     * @param transcript TranscriptOld
     * @returns a unix timestamp for the processed_time
     */
    getProcessedTime: (transcript: TranscriptOld): number => {
        let start = new Date(new Date().getTime() - 4 * 60000).getTime()
        if (transcript.recordingStartedAt && typeof transcript.processedTime === 'number') {
            start = new Date(transcript.recordingStartedAt.getTime() + transcript.processedTime * 1000).getTime()
        }

        return start
    },

    mergeAsrChunk: (currentTranscript: TranscriptOld, asrChunkJSON: ASRChunkJSON, preserveDiff?: boolean): TranscriptOld =>
        produce(currentTranscript, (nextTranscript) => {
            let firstModifiedIndex = nextTranscript.asrParagraphs.length - 1
            let p = nextTranscript.asrParagraphs[firstModifiedIndex]

            /** clean all the data (paragraphs & words) that are related to the utterance of asrChunkJSON
             *  because each chunk contains all the most updated content for that utterance **/
            while (p?.uttIds.includes(asrChunkJSON.utt_id)) {
                if (!nextTranscript.words[p.id]) {
                    console.log('Transcript', JSON.stringify(nextTranscript))
                    console.log('ASR chunk', JSON.stringify(asrChunkJSON))
                    rollbarService.error('[Merge ASR chunk] Paragraph words not found')
                    nextTranscript.words[p.id] = []
                }
                p.uttIds = nextTranscript.words[p.id].reduce<number[]>(
                    (acc, w) => (!acc.includes((w as ASRTranscriptWord).uttId) ? acc.concat((w as ASRTranscriptWord).uttId) : acc),
                    [],
                )

                /** if all the words of this paragraph are from that utterance, remove the paragraph along with it's words **/
                if (p.uttIds.length === 1) {
                    nextTranscript.asrParagraphs.pop()
                    delete nextTranscript.words[p.id]
                } else {
                    /** if some of the words of this paragraph are from that utterance, remove only these words **/
                    nextTranscript.words[p.id] = nextTranscript.words[p.id].filter(
                        (w) => (w as ASRTranscriptWord).uttId !== asrChunkJSON.utt_id,
                    )

                    const pLastWord = last(nextTranscript.words[p.id])!
                    const pEndTime = pLastWord.time + pLastWord.duration

                    p.uttIds = p.uttIds.filter((uttId) => uttId !== asrChunkJSON.utt_id)
                    p.duration = pEndTime - p.time
                    p.words = nextTranscript.words[p.id]
                    p.updatedAt = Date.now()
                }

                firstModifiedIndex--
                p = nextTranscript.asrParagraphs[firstModifiedIndex]
            }

            firstModifiedIndex = Math.max(0, firstModifiedIndex)

            /** process asrChunkJSON words **/
            asrChunkJSON.word_list.forEach((w) => {
                const lastRevisionParagraph = last(nextTranscript.paragraphs)
                const lastRevisionWord = last(nextTranscript.words[lastRevisionParagraph?.id ?? ''])
                const lastAsrParagraph = last(nextTranscript.asrParagraphs)
                const lastAsrWord = last(nextTranscript.words[lastAsrParagraph?.id ?? ''])
                const lastRevisionWordEndTime = (lastRevisionWord?.time ?? 0) + (lastRevisionWord?.duration ?? 0)
                const revisionSectionEndTime = Math.max(nextTranscript.processedTime, lastRevisionWordEndTime)
                const asrSectionEndTime = (lastAsrWord?.time ?? 0) + (lastAsrWord?.duration ?? 0)

                if (
                    w.start < Math.max(revisionSectionEndTime, asrSectionEndTime) ||
                    (w.start === revisionSectionEndTime && isPunctuation(w.text))
                ) {
                    return
                }

                /** if this word belongs to the last paragraph, add it to it's words array **/
                if (w.paragraph_id === lastAsrParagraph?.id) {
                    nextTranscript.words[w.paragraph_id].push(TranscriptWord.fromAsrChunkWordJSON(w, asrChunkJSON.utt_id))

                    lastAsrParagraph.authorId = w.speaker_id
                    lastAsrParagraph.uttIds = uniq([...lastAsrParagraph.uttIds, asrChunkJSON.utt_id])
                    lastAsrParagraph.duration = w.end - nextTranscript.asrParagraphs[0].time
                    lastAsrParagraph.words = nextTranscript.words[w.paragraph_id]
                    lastAsrParagraph.updatedAt = Date.now()
                } else {
                    /** if this word doesn't belong to the last paragraph, create a new paragraph with this word and add it to the paragraphs array **/

                    nextTranscript.words[w.paragraph_id] = [TranscriptWord.fromAsrChunkWordJSON(w, asrChunkJSON.utt_id)]

                    nextTranscript.asrParagraphs.push({
                        id: w.paragraph_id,
                        uttIds: [asrChunkJSON.utt_id],
                        authorId: w.speaker_id,
                        time: w.start,
                        timeAbsolute: asrChunkJSON.start_epoch,
                        duration: w.end - w.start,
                        words: nextTranscript.words[w.paragraph_id],
                        updatedAt: Date.now(),
                    })
                }

                if (!nextTranscript.authors[w.speaker_id]) {
                    nextTranscript.authors[w.speaker_id] = {
                        id: w.speaker_id,
                        name: w.speaker_label,
                    }
                }
            })

            nextTranscript.asrProcessedTime = asrChunkJSON.end

            /** calculate the diff between the prev content before merging the chunk to the next content after merging the chunk **/
            if (currentTranscript) {
                const removed = currentTranscript.asrParagraphs.slice(firstModifiedIndex, currentTranscript.asrParagraphs.length)
                const added = nextTranscript.asrParagraphs.slice(firstModifiedIndex, nextTranscript.asrParagraphs.length)

                if (preserveDiff && nextTranscript.lastAsrUpdateDiff) {
                    nextTranscript.lastAsrUpdateDiff.removed.push(...removed)
                    nextTranscript.lastAsrUpdateDiff.added.push(...added)
                } else {
                    nextTranscript.lastAsrUpdateDiff = {
                        removed,
                        added,
                    }
                }
            }

            if (asrChunkJSON.utt_ended) {
                if (currentTranscript?.unfinishedUtteranceId === asrChunkJSON.utt_id) {
                    nextTranscript.unfinishedUtteranceId = null
                }
            } else {
                nextTranscript.unfinishedUtteranceId = asrChunkJSON.utt_id
            }
        }),

    mergeAsrTranscriptionHistory: (transcript: TranscriptOld, asrUpdateStreamMessages: ASRUpdateStreamMessage[]) =>
        asrUpdateStreamMessages
            .filter((item) => item.data.utt_ended)
            .reverse()
            .reduce((accTranscript, { data: asrChunkJSON }) => TranscriptOld.mergeAsrChunk(accTranscript, asrChunkJSON), transcript),

    // no-ops
    fromOldStructure: () => TranscriptOld.EMPTY,
}
